// --- Core ---
import {each, find, last, map, pluck} from "underscore";
// --- SaniQ ---
import {Cron, defaultCron, parseMultilineCron} from "helpers/forms/Cron";
import {parseOptionalISO} from "helpers/Parser";
import {toCamel} from "helpers/StringUtil";
import {keyOf, runInCtx} from "./DeserializeContext";

interface JSONAPIReference<T extends string = string> {
  id: string;
  type: T;
  included: false;
}

interface JSONAPIRecord extends JSONAPIReference {
  attributes: {
    [key: string]: string | number | object | Array<string | number | object>
  };
  relationships?: {
    [key: string]: JSONAPIRelationship;
  };
}

interface JSONAPIContent<T = JSONAPIData> {
  data: T;
  included?: JSONAPIIncluded;
}

interface JSONAPICollectionContent {
  data: JSONAPIRecord[];
  included?: JSONAPIIncluded;
}

const isJSONAPICollection = (content: JSONAPIContent): content is JSONAPICollectionContent => content.data && Array.isArray(content.data);
type JSONAPIData = JSONAPIRecord | JSONAPIRecord[];
type JSONAPIIncluded = JSONAPIRecord[];
type JSONAPIManyRelationship = {
  data: JSONAPIReference[];
};
type JSONAPISingleRelationship = {
  data: JSONAPIReference;
};
type JSONAPIRelationship = JSONAPIManyRelationship | JSONAPISingleRelationship;

interface DeserializedRecord<T extends string = string> {
  id: string;
  type: T;
  included?: boolean;
  itemReference?: string;
  createdAt?: Date;
  updatedAt?: Date;
  /**
   * When a record may not be deleted, set this
   */
  deleteDisabled?: boolean;
  /**
   * When set in the Deserializer, only paranoid delete is allowed. This attribute is e.g used by DeleteConfirmDialog
   */
  finalDeleteDisabled?: boolean;
  //NOTE(Keune): I don't know yet of a better way to define additional custom Authors for the versions
  additionalVersionAuthors?: unknown;
}

type OmitSerialized<T extends DeserializedRecord> = Omit<T, "id" | "type" | "included" | "itemReference">;
type Deserializer<T extends DeserializedRecord = DeserializedRecord, U extends JSONAPIRecord = JSONAPIRecord> =
  (data: U, included?: JSONAPIIncluded) => T;

/* NOTE(arturschens) Guard vor zyklischen Aufrufen der Deserializer wie A->B->A->B */
const findAndDeserializeItem =
  <T extends DeserializedRecord = DeserializedRecord>
  (data: JSONAPIReference, included: JSONAPIIncluded, deserializer: Deserializer<T> = deserializeItem): T =>
    runInCtx((ctx) => {
      const k = keyOf(data.type, data.id);
      // wenn bereits besucht
      if (ctx.cache.has(k)) return ctx.cache.get(k) as T;
      // zyklus erkannt, gebe deserializeItem zurück und bleibe shallow
      if (ctx.building.has(k)) {
        return deserializeItem(data as JSONAPIRecord) as T;
      }
      const item = find(included, include => include.type === data.type && include.id === data.id);

      // Wenn nicht im included: deserializeItem + cache
      if (!item) {
        const stub = deserializeItem(data as JSONAPIRecord) as T;
        ctx.cache.set(k, stub);
        return stub;
      }
      // normaler Build mit Guard
      ctx.building.add(k);
      const value = deserializer(item, included);
      ctx.building.delete(k);
      ctx.cache.set(k, value);
      return value;
    });

const findAndDeserializeRelationship = <T extends DeserializedRecord = DeserializedRecord>(
  relationship: JSONAPIRelationship,
  included: JSONAPIIncluded,
  deserializer: Deserializer<T> = deserializeItem): T | T[] =>
    runInCtx(() => {
      if (relationship.data) {
        if (Array.isArray(relationship.data)) {
          return map(relationship.data, (relation) => findAndDeserializeItem<T>(relation, included, deserializer));
        } else {
          return findAndDeserializeItem<T>(relationship.data, included, deserializer);
        }
      }

      return undefined;
    });

const deserializeManyRelationship = <T extends DeserializedRecord = DeserializedRecord>(relationship: {
  data: JSONAPIReference[]
}, included: JSONAPIIncluded = [], deserializer: Deserializer<T> = deserializeItem): T[] =>
    runInCtx(() => {
      if (relationship?.data) {
        return map(relationship.data, (relation) => findAndDeserializeItem<T>(relation, included, deserializer));
      }

      return undefined;
    });

const deserializeSingleRelationship = <T extends DeserializedRecord = DeserializedRecord>(relationship: {
  data: JSONAPIReference
}, included: JSONAPIIncluded = [], deserializer: Deserializer<T> = deserializeItem): T =>
    runInCtx(() => {
      if (relationship?.data) {
        return findAndDeserializeItem<T>(relationship.data, included, deserializer);
      }

      return undefined;
    });

/**
 * Compose a reference for a deserialized item
 *
 * This is especially useful when a form has a reference to polymorphic items. In that case the id might not be unique
 *
 * @param reference The item to which a reference is needed
 * @returns Reference if item is defined
 * @see decomposeReference
 */
const composeReference = (reference?: JSONAPIReference): string | undefined => reference ? `${reference.type}#${reference.id}` : undefined;

/**
 * Produce type and id from a previously generated reference
 *
 * @param reference The composed reference
 * @returns Decomposed reference when defined
 * @see composeReference
 */
const decomposeReference = (reference: string): JSONAPIReference | undefined => {
  if (reference) {
    const [type, id] = reference.split('#');

    return {
      id: id,
      type: type
    } as JSONAPIReference;
  }
};

type Flatten<Type> = Type extends Array<infer Item> ? Item : Type;

const deserializeContent = <T extends DeserializedRecord>(
  content: JSONAPIContent,
  deserializer: Deserializer<T> = deserializeItem<T>
): T | T[] =>
    runInCtx(() => {
      const {included} = content;

      if (isJSONAPICollection(content)) {
        return content.data.map(d => deserializer(d, included));
      } else {
        return deserializer(content.data as JSONAPIRecord, included);
      }
    });

type AttributeValueParser = (value: string | number | object | unknown[], key: string) => [string, string | number | object | Date | Cron[]];

/**
 * Parse a value according to its key.
 *
 * - Cron descriptors are converted to a Cron array
 *   - A cron descriptor has the suffix '_cron'
 * - Timestamps are converted to Date
 *   - A timestamp has the suffix '_at' or '_date'
 * - Unix timestamps are converted to Date
 *   - A unix timestamp has the suffix '_ts'
 */
const parseAttributeValue: AttributeValueParser = (value, key) => {
  const keyEnding = last(key.split("_"));
  let parsedKey = toCamel(key);
  let parsedValue = value;

  switch (keyEnding) {
    case 'at':
    case 'from':
    case 'to':
    case 'date':
      parsedValue = parseOptionalISO(value);
      break;
    case 'cron':
      parsedValue = parseMultilineCron(value as string, defaultCron);
      break;
    case 'ts':
      parsedKey = toCamel(key.replace('_ts', ''));
      if (value === 0 || value === null || value === "") {
        parsedValue = null;
      } else {
        parsedValue = new Date(value as number * 1000);
      }
      break;
    default:
      break;
  }

  return [parsedKey, parsedValue];
};

/**
 * Convert a serialized hash to a representation with camelCase keys and parsed values.
 *
 * Similar to deserializeItem, but when an attribute is a hash
 */
const deserializeObject = <T,>(data, parse: AttributeValueParser = parseAttributeValue): T => {
  const result = {} as T;

  each(data, (value, key) => {
    const [parsedKey, parsedValue] = parse(value, key);
    result[parsedKey] = parsedValue;
  });

  return result;
};

const deserializeItem = <T extends DeserializedRecord = DeserializedRecord>(data: JSONAPIRecord): T => {
  const result = {
    id: data.id,
    type: data.type,
    included: true,
    itemReference: composeReference(data)
  } as T;

  //NOTE(Keune): Convert attribute names to camelCase and parse its value
  each(data.attributes, (value, key) => {
    const [parsedKey, parsedValue] = parseAttributeValue(value, key);
    result[parsedKey] = parsedValue;
  });

  return result;
};

const deserializeItemWithRelationships: Deserializer = (data, included) =>
  runInCtx(() => {
    const result = {
      ...deserializeItem(data)
    };

    each(data.relationships, (relationship, key) => {
      if (Array.isArray(relationship.data)) {
        result[toCamel(key)] = deserializeManyRelationship(relationship as JSONAPIManyRelationship, included);
      } else {
        result[toCamel(key)] = deserializeSingleRelationship(relationship as JSONAPISingleRelationship, included);
      }
    });

    return result;
  });

const deserializeAssignments = <T extends DeserializedRecord = DeserializedRecord>(relationships: string[]) => (data: JSONAPIRecord): T => {
  const result = deserializeItem<T>(data);

  each(relationships, relationship => {
    if (data.relationships[relationship]) {
      if (Array.isArray(data.relationships[relationship].data)) {
        result[`${toCamel(relationship)}Ids`] = pluck(data.relationships[relationship].data, 'id');
      } else {
        result[`${toCamel(relationship)}Id`] = (data.relationships[relationship].data as JSONAPIReference).id;
      }
    }
  });

  return result;
};

export type {
  DeserializedRecord,
  JSONAPIContent,
  JSONAPIManyRelationship,
  JSONAPISingleRelationship,
  JSONAPIRecord,
  JSONAPIReference,
  OmitSerialized,
  Flatten
};

export {
  AttributeValueParser,
  composeReference,
  decomposeReference,
  deserializeObject,
  isJSONAPICollection,
  parseAttributeValue,
  deserializeContent,
  deserializeAssignments,
  deserializeItem,
  deserializeItemWithRelationships,
  deserializeManyRelationship,
  deserializeSingleRelationship,
  Deserializer,
  findAndDeserializeRelationship
};
